import { IconButton, InputAdornment, Tooltip } from '@mui/material';
import TextField, { CustomTextFieldProps } from '../../inputs/Textfield';

import { TID_ATTR } from '@/common/infrastructure/config/tid';
import { FormikFieldSkeleton } from './FormikFieldSkeleton';
import { FormikFieldWithTooltipProps } from './types/FormikFieldProps';
import { alpha } from '@mui/material/styles';
import { useFormikField } from './hooks/useFormikField';
import { getFieldPlaceholder, getInputValue } from './utils/fieldUtils';

export const FormikTextField = <T extends Record<string, unknown>>({
  id,
  label,
  tooltipText,
  tooltipPlacement,
  iconEndAdornment,
  placeholder,
  highlighted,
  rows,
  required,
  value: explicitValue,
  testId,
  transformOnChange,
  ...rest
}: FormikFieldWithTooltipProps<T> & CustomTextFieldProps & { testId?: string; transformOnChange?: (value: string) => string }) => {
  const {
    [TID_ATTR]: restTestId,
    onChange: externalOnChange,
    ...restWithoutTestId
  } = rest as Record<string, unknown> & {
    [TID_ATTR]?: string;
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
  };
  const baseTestId = testId ?? restTestId;
  const { status, handleChange, handleBlur, setFieldValue, setFieldTouched, errorText, hasError, fieldValue } = useFormikField<T>({ id });

  const actualValue = getInputValue(fieldValue, explicitValue);
  const actualPlaceholder = getFieldPlaceholder({
    fieldValue,
    explicitValue,
    customPlaceholder: placeholder,
    label
  });

  const existingSlotProps = (restWithoutTestId.slotProps ?? {}) as Record<string, Record<string, unknown> | undefined>;
  const inputPropsFromRest = (restWithoutTestId.inputProps ?? {}) as Record<string, unknown>;
  const existingInputProps = (restWithoutTestId.InputProps ?? {}) as { endAdornment?: React.ReactNode };
  const existingInputLabelProps = (restWithoutTestId.InputLabelProps ?? {}) as Record<string, unknown>;

  const endAdornmentTestId = baseTestId ? { [TID_ATTR]: `${baseTestId}:end_adornment` } : {};
  const endAdornment = iconEndAdornment ? (
    <InputAdornment position="end" {...endAdornmentTestId}>
      {tooltipText ? (
        <Tooltip title={tooltipText} arrow placement={tooltipPlacement}>
          <IconButton>{iconEndAdornment}</IconButton>
        </Tooltip>
      ) : (
        iconEndAdornment
      )}
    </InputAdornment>
  ) : (
    (existingInputProps.endAdornment ?? null)
  );

  const mergedSlotProps = {
    ...existingSlotProps,
    ...(baseTestId && {
      inputLabel: {
        ...existingInputLabelProps,
        ...existingSlotProps.inputLabel,
        [TID_ATTR]: `${baseTestId}:label`
      },
      htmlInput: {
        ...inputPropsFromRest,
        ...existingSlotProps.htmlInput,
        [TID_ATTR]: `${baseTestId}:input`
      },
      formHelperText: {
        ...existingSlotProps.formHelperText,
        [TID_ATTR]: `${baseTestId}:helper-text`
      }
    }),
    input: {
      ...(typeof existingSlotProps.input === 'object' && existingSlotProps.input !== null ? existingSlotProps.input : {}),
      ...existingInputProps,
      endAdornment
    }
  };

  if (status?.loading) {
    return <FormikFieldSkeleton />;
  }

  const handleChangeOrTransform = transformOnChange
    ? (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const next = transformOnChange(e.target.value);
        setFieldValue(id as keyof T & string, next);
        setFieldTouched(id as keyof T & string, true);
      }
    : handleChange;

  return (
    <TextField
      {...restWithoutTestId}
      fullWidth
      id={id}
      name={id}
      label={label}
      placeholder={actualPlaceholder}
      value={actualValue}
      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        handleChangeOrTransform(e);
        externalOnChange?.(e as React.ChangeEvent<HTMLInputElement>);
      }}
      onBlur={handleBlur}
      error={hasError}
      helperText={errorText}
      rows={rows}
      required={required}
      slotProps={mergedSlotProps as CustomTextFieldProps['slotProps']}
      sx={{
        transition: 'all 0.3s ease',
        '& .MuiOutlinedInput-root': {
          transition: 'all 0.3s ease',
          ...(highlighted && {
            background: (theme) => alpha(theme.palette.info.light, 0.05),
            boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.info.light, 0.1)}`
          })
        },
        '& .MuiInputAdornment-root': {
          alignSelf: rows && Number(rows) > 1 ? 'flex-start' : 'center'
        },
        ...(restWithoutTestId.sx as object)
      }}
    />
  );
};
